import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { differenceInYears } from 'date-fns';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import ImageIcon from '@mui/icons-material/Image';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { BooleanFilter } from '@e-socna/utils';
import type {
  CivilityId,
  FragmentType,
  ThirdPartyCategoryId,
} from '@e-socna/data-layer';

import { formatDate } from '../../utils/format-date.util';
import { Memo } from '../../components/Memo';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { PersonForm } from '../../forms/ThirdParty/Person';
import { Avatar } from '../../components/Avatar';
import { thirdPartyTooltipOf } from '../../utils/third-party-tooltip.util';
import { Code } from '../../components/Code';

export const PersonPage_GeneralInfo_Person_Fragment = graphql(`
  fragment PersonPage_GeneralInfo_Person_Fragment on Person {
    _id
    code
    codingConfig {
      kind
      strategyId
      ... on CodingConfigManual {
        code
      }
      ...CodeCmp_CodingConfig_Fragment
    }
    internalMemo
    memo
    isInternal
    isVip
    civilityId
    birthDate
    signatureImageUri
    pictureImageUri
    color
    fullName
    categoryIds
    ...ThirdPartyForm_Person_Fragment
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    edit: string;
  }>;
  readonly person: FragmentType<typeof PersonPage_GeneralInfo_Person_Fragment>;
}

export const GeneralInfo = ({ paths, person: pPerson }: Readonly<Props>) => {
  const person = getFragmentData(
    PersonPage_GeneralInfo_Person_Fragment,
    pPerson,
  );

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const [isSignatureDrawerOpen, setIsSignatureDrawerOpen] = useState(false);
  const [isPictureDrawerOpen, setIsPictureDrawerOpen] = useState(false);

  const openSignatureDrawer = useCallback(() => {
    setIsSignatureDrawerOpen(true);
  }, []);

  const closeSignatureDrawer = useCallback(() => {
    setIsSignatureDrawerOpen(false);
  }, []);

  const openPictureDrawer = useCallback(() => {
    setIsPictureDrawerOpen(true);
  }, []);

  const closePictureDrawer = useCallback(() => {
    setIsPictureDrawerOpen(false);
  }, []);

  const { civilitiesIdMap, thirdPartyCategoriesIdMap } = useReferencesContext();

  const civilityLabel = civilitiesIdMap[person.civilityId as CivilityId]?.label;

  const categories = person.categoryIds
    ?.map(
      (categoryId) =>
        thirdPartyCategoriesIdMap[categoryId as ThirdPartyCategoryId]?.label,
    )
    .filter(BooleanFilter);

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Memo
          content={{
            internal: person.internalMemo,
            memo: person.memo,
          }}
          display={{ internal: true, memo: true }}
        />
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code code={person.code} codingConfig={person.codingConfig} />
            {person.isInternal ? (
              <Typography gutterBottom variant="body1">
                Tiers interne : Oui
              </Typography>
            ) : (
              <Typography gutterBottom variant="body1">
                VIP : {person.isVip ? 'Oui' : 'Non'}
              </Typography>
            )}
            <Typography gutterBottom variant="body1">
              Catégories :{' '}
              {categories?.length ? categories.join(', ') : 'Aucune'}
            </Typography>
            <Typography gutterBottom variant="body1">
              {/*eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing*/}
              Civilité : {civilityLabel || 'Inconnue'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Nom complet : {person.fullName}
            </Typography>
            <Typography gutterBottom variant="body1">
              Date de naissance :{' '}
              {person.birthDate
                ? `${formatDate(person.birthDate)} (${differenceInYears(
                    new Date(),
                    new Date(person.birthDate),
                  ).toFixed()} ans)`
                : 'Inconnue'}
            </Typography>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!person.isInternal && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ mr: 1 }} variant="body1">
                  Signature :
                </Typography>
                {person.signatureImageUri ? (
                  <Button
                    onClick={openSignatureDrawer}
                    size="small"
                    startIcon={<ImageIcon />}
                    variant="outlined"
                  >
                    Voir la signature
                  </Button>
                ) : (
                  <Typography variant="body1">Inconnue</Typography>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ mr: 1 }} variant="body1">
                Photo de profil :
              </Typography>
              {person.pictureImageUri ? (
                <Button
                  onClick={openPictureDrawer}
                  size="small"
                  startIcon={<ImageIcon />}
                  variant="outlined"
                >
                  Voir la photo de profil
                </Button>
              ) : (
                <Typography variant="body1">Inconnue</Typography>
              )}
            </Box>
            <Avatar
              bgColor={person.color}
              fullName={person.fullName}
              imageUri={person.pictureImageUri}
              label={thirdPartyTooltipOf(person.fullName)}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            />
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editPersonGeneralInfo',
            icon: <EditIcon />,
            name: 'Modifier Infos Générales',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Générales de la Personne"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <PersonForm
          formVariant={FormVariant.Modification}
          initialValues={person}
          isInternal={person.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          personId={person._id}
          stepIdsSubset={[ThirdPartyFormTab.GeneralInfo]}
          title="Formulaire de modification des Informations Générales de la Personne"
        />
      </RightDrawer>
      {!!person.signatureImageUri && (
        <RightDrawer
          isOpen={isSignatureDrawerOpen}
          onDrawerClose={closeSignatureDrawer}
        >
          <Box sx={{ flexGrow: 1, p: 2 }}>
            <Typography gutterBottom variant="h6">
              Signature
            </Typography>
            <Box
              component="img"
              src={person.signatureImageUri}
              sx={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </RightDrawer>
      )}
      {!!person.pictureImageUri && (
        <RightDrawer
          isOpen={isPictureDrawerOpen}
          onDrawerClose={closePictureDrawer}
        >
          <Box sx={{ flexGrow: 1, p: 2 }}>
            <Typography gutterBottom variant="h6">
              Photo de profil
            </Typography>
            <Box
              component="img"
              src={person.pictureImageUri}
              sx={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </RightDrawer>
      )}
    </>
  );
};
